import React, { useEffect, useMemo, useState } from "react";
import { createMappingBranch, getMappingBranchViewList, updateMappingBranch } from "../api/mappingBranchAPI";
import { getEnterprises } from "../api/enterpriseApi";
import "./EnterprisesPage.css";

const emptyValue = "—";

const formatDate = (value) => {
    if (!value) {
        return emptyValue;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("uk-UA");
};

const EntTile = ({ label, value }) => (
    <div className="ent-tile">
        <span className="l">{label}</span>
        <span className="v">{value || emptyValue}</span>
    </div>
);

const MappingBranchPage = ({ embedded = false }) => {
    const [enterprises, setEnterprises] = useState([]);
    const [mappingRecords, setMappingRecords] = useState([]);
    const [selectedEnterpriseCode, setSelectedEnterpriseCode] = useState("");
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [editError, setEditError] = useState("");
    const [editSuccess, setEditSuccess] = useState("");
    const [editingBranch, setEditingBranch] = useState(null);
    const [editingStoreId, setEditingStoreId] = useState("");
    const [editingGoogleFolderId, setEditingGoogleFolderId] = useState("");

    const [branch, setBranch] = useState("");
    const [storeId, setStoreId] = useState("");
    const [googleFolderId, setGoogleFolderId] = useState("");

    const loadMappingViewList = async () => {
        setListLoading(true);
        setListError("");
        try {
            const data = await getMappingBranchViewList();
            setMappingRecords(data);
        } catch (error) {
            console.error("Error fetching mapping branch view list:", error);
            setListError("Не удалось загрузить список маппинга филиалов.");
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        async function fetchInitialData() {
            try {
                const enterpriseData = await getEnterprises();
                setEnterprises(enterpriseData);

                const firstEnterpriseCode = enterpriseData.find((item) => item.data_format && item.data_format !== "Blank")?.enterprise_code
                    || enterpriseData[0]?.enterprise_code
                    || "";
                setSelectedEnterpriseCode(firstEnterpriseCode);
            } catch (error) {
                console.error("Error fetching enterprises:", error);
            }

            await loadMappingViewList();
        }

        fetchInitialData();
    }, []);

    const selectedEnterprise = useMemo(
        () => enterprises.find((enterprise) => enterprise.enterprise_code === selectedEnterpriseCode) || null,
        [enterprises, selectedEnterpriseCode]
    );

    const filteredMappings = useMemo(
        () => mappingRecords.filter((item) => item.enterprise_code === selectedEnterpriseCode),
        [mappingRecords, selectedEnterpriseCode]
    );

    const editingMapping = useMemo(
        () => filteredMappings.find((item) => item.branch === editingBranch) || null,
        [filteredMappings, editingBranch]
    );

    const handleSave = async () => {
        setSaveError("");
        if (!branch || !storeId || !selectedEnterpriseCode || !googleFolderId) {
            return;
        }

        const mappingData = {
            branch,
            store_id: storeId,
            enterprise_code: selectedEnterpriseCode,
            google_folder_id: googleFolderId.trim() || null,
            id_telegram: [],
        };

        try {
            await createMappingBranch(mappingData);
            setBranch("");
            setStoreId("");
            setGoogleFolderId("");
            alert("Запись успешно добавлена.");
            await loadMappingViewList();
        } catch (error) {
            console.error("Error saving mapping branch:", error);
            setSaveError("Ошибка при сохранении маппинга филиала.");
        }
    };

    const startEdit = (item) => {
        setEditError("");
        setEditSuccess("");
        setEditingBranch(item.branch);
        setEditingStoreId(item.store_mapping_value || "");
        setEditingGoogleFolderId(item.google_folder_id || "");
    };

    const cancelEdit = () => {
        setEditError("");
        setEditSuccess("");
        setEditingBranch(null);
        setEditingStoreId("");
        setEditingGoogleFolderId("");
    };

    const handleUpdate = async () => {
        setEditError("");
        setEditSuccess("");

        const normalizedStoreId = editingStoreId.trim();
        if (!editingBranch || !normalizedStoreId) {
            setEditError("Поле 'Внешний store / mapping' должно быть заполнено.");
            return;
        }

        try {
            const updated = await updateMappingBranch(editingBranch, {
                store_id: normalizedStoreId,
                google_folder_id: editingGoogleFolderId.trim() || null,
            });
            setEditSuccess("Изменения сохранены.");
            setEditingGoogleFolderId(updated.google_folder_id || "");
            setEditingStoreId(updated.store_id || "");
            await loadMappingViewList();
        } catch (error) {
            console.error("Error updating mapping branch:", error);
            setEditError("Ошибка при обновлении маппинга филиала.");
        }
    };

    return (
        <div className="ent-page">
            {!embedded && (
                <div className="ent-card" style={{ padding: "20px 24px" }}>
                    <h1 style={{ margin: 0, fontSize: "26px" }}>Маппинг филиалов</h1>
                </div>
            )}

            <div className="ent-card ent-toolbar">
                <div className="th">
                    <div>
                        <h2>Маппинг филиалов</h2>
                        <p>Связь между филиалом предприятия (branch) и внешним store / mapping value — используется при синхронизации каталога, остатков и Telegram-уведомлений.</p>
                    </div>
                    <div className="acts">
                        <div className="ent-fld" style={{ minWidth: "260px" }}>
                            <label>Предприятие</label>
                            <select
                                className="ent-inp"
                                value={selectedEnterpriseCode}
                                onChange={(e) => setSelectedEnterpriseCode(e.target.value)}
                            >
                                <option value="">-- Выберите предприятие --</option>
                                {enterprises.map((enterprise) => (
                                    <option key={enterprise.enterprise_code} value={enterprise.enterprise_code}>
                                        {enterprise.enterprise_name} ({enterprise.enterprise_code})
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="ent-split">
                <aside className="ent-card ent-picker">
                    <h2>Добавить маппинг</h2>
                    <div className="ent-fld">
                        <label>Предприятие</label>
                        <input
                            className="ent-inp"
                            type="text"
                            value={selectedEnterprise ? `${selectedEnterprise.enterprise_name} (${selectedEnterprise.enterprise_code})` : ""}
                            disabled
                        />
                    </div>
                    <div className="ent-fld">
                        <label>Филиал</label>
                        <input
                            className="ent-inp"
                            type="text"
                            placeholder="Например: 30555"
                            value={branch}
                            onChange={(e) => setBranch(e.target.value)}
                        />
                    </div>
                    <div className="ent-fld">
                        <label>Внешний store / mapping</label>
                        <input
                            className="ent-inp"
                            type="text"
                            placeholder="Введите store_id"
                            value={storeId}
                            onChange={(e) => setStoreId(e.target.value)}
                        />
                    </div>
                    <div className="ent-fld">
                        <label>Google Folder ID</label>
                        <input
                            className="ent-inp"
                            type="text"
                            placeholder="Введите Google Folder ID (необязательно)"
                            value={googleFolderId}
                            onChange={(e) => setGoogleFolderId(e.target.value)}
                        />
                    </div>

                    {saveError ? <div className="ent-msg err">{saveError}</div> : null}

                    <button
                        type="button"
                        className="ent-btn ent-btn-primary ent-btn-block"
                        onClick={handleSave}
                        disabled={!branch || !storeId || !selectedEnterpriseCode}
                    >
                        Записать
                    </button>
                </aside>

                <section className="ent-detail">
                    <div className="ent-card" style={{ padding: "18px 20px" }}>
                        <div className="ent-bh" style={{ marginBottom: "12px" }}><h4>Информация по предприятию</h4></div>
                        {selectedEnterprise ? (
                            <div className="ent-grid3">
                                <EntTile label="Название" value={selectedEnterprise.enterprise_name} />
                                <EntTile label="Код предприятия" value={selectedEnterprise.enterprise_code} />
                                <EntTile label="Формат" value={selectedEnterprise.data_format} />
                                <EntTile label="Посл. загрузка каталога" value={formatDate(selectedEnterprise.last_catalog_upload)} />
                                <EntTile label="Посл. загрузка остатков" value={formatDate(selectedEnterprise.last_stock_upload)} />
                            </div>
                        ) : (
                            <p className="ent-mini">Выберите предприятие, чтобы увидеть краткую информацию.</p>
                        )}
                    </div>

                    <div className="ent-card" style={{ padding: "18px 20px" }}>
                        <div className="ent-bh" style={{ marginBottom: "12px" }}>
                            <h4>Редактировать маппинг</h4>
                            {editingMapping ? <span className="hint">филиал {editingMapping.branch}</span> : null}
                        </div>
                        {!editingMapping ? (
                            <p className="ent-mini">Выберите строку в списке ниже и нажмите «Редактировать».</p>
                        ) : (
                            <>
                                <div className="ent-grid2">
                                    <EntTile label="Предприятие" value={editingMapping.enterprise_display_label} />
                                    <EntTile label="Филиал" value={editingMapping.branch} />
                                </div>

                                <div className="ent-grid2" style={{ marginTop: "12px" }}>
                                    <div className="ent-fld">
                                        <label>{editingMapping.semantic_store_label}</label>
                                        <input
                                            className="ent-inp"
                                            type="text"
                                            value={editingStoreId}
                                            onChange={(e) => setEditingStoreId(e.target.value)}
                                        />
                                    </div>
                                    <div className="ent-fld">
                                        <label>Google Folder ID</label>
                                        <input
                                            className="ent-inp"
                                            type="text"
                                            value={editingGoogleFolderId}
                                            onChange={(e) => setEditingGoogleFolderId(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <p className="ent-mini" style={{ marginTop: "10px" }}>
                                    Поля «Филиал» и «Предприятие» не редактируются на этом шаге.
                                </p>

                                {editError ? <div className="ent-msg err">{editError}</div> : null}
                                {editSuccess ? <div className="ent-msg ok">{editSuccess}</div> : null}

                                <div className="ent-save-bar">
                                    <button type="button" className="ent-btn ent-btn-ghost" onClick={cancelEdit}>Отмена</button>
                                    <button type="button" className="ent-btn ent-btn-primary" onClick={handleUpdate}>Сохранить</button>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="ent-card" style={{ overflow: "hidden" }}>
                        <div className="ent-toolbar" style={{ borderBottom: "1px solid var(--ent-border)" }}>
                            <div className="th">
                                <div>
                                    <h2 style={{ fontSize: "15px" }}>Список маппинга</h2>
                                    <p>Показаны только записи для выбранного предприятия.</p>
                                </div>
                                <div className="acts">
                                    <button type="button" className="ent-btn ent-btn-ghost ent-btn-sm" onClick={loadMappingViewList}>Обновить</button>
                                </div>
                            </div>
                        </div>

                        {listLoading ? (
                            <div className="ent-empty">Загрузка списка…</div>
                        ) : listError ? (
                            <div className="ent-empty ent-msg err">{listError}</div>
                        ) : !selectedEnterpriseCode ? (
                            <div className="ent-empty">Сначала выберите предприятие.</div>
                        ) : filteredMappings.length === 0 ? (
                            <div className="ent-empty">Для выбранного предприятия записи маппинга пока не найдены.</div>
                        ) : (
                            <div className="ent-tbl-wrap" style={{ border: 0, borderRadius: 0 }}>
                                <table className="ent-tbl">
                                    <thead>
                                        <tr>
                                            <th>Филиал</th>
                                            <th>Store / mapping</th>
                                            <th>Google Folder ID</th>
                                            <th>Telegram</th>
                                            <th>Статус</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredMappings.map((item) => (
                                            <tr key={item.mapping_key} className={editingBranch === item.branch ? "sel" : ""}>
                                                <td className="strong">{item.branch}</td>
                                                <td>{item.store_mapping_value || emptyValue}</td>
                                                <td>{item.google_folder_id || emptyValue}</td>
                                                <td>
                                                    <span className={item.has_telegram_target ? "ent-badge on" : "ent-badge off"}>
                                                        {item.has_telegram_target ? <span className="dot" /> : null}
                                                        {item.has_telegram_target ? "Есть" : "Нет"}
                                                    </span>
                                                </td>
                                                <td>
                                                    {item.conflict_flags.length > 0 ? (
                                                        <span className="ent-pill warn">Конфликт</span>
                                                    ) : (
                                                        <span className="ent-pill g">Ок</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <button type="button" className="ent-btn ent-btn-ghost ent-btn-sm" onClick={() => startEdit(item)}>
                                                        Редактировать
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default MappingBranchPage;
